import type { Security } from '@ipfabric/utils';

/**
 * The chains traversed to reach `chainName`: from the machine's entry chain down
 * to, but excluding, the chain itself. Empty for the entry chain and for one
 * nothing `exec`s (an orphan, or a name not in the machine).
 *
 * The entity list offers every chain flat, including ones only reachable
 * *through* another — a GCP enforcement point runs one chain per firewall
 * policy. This rebuilds the trail so opening a chain from that list reads the
 * same as arriving from a decision's rule path. A chain reachable by several
 * paths gets the first one found.
 */
export const ruleChainAncestors = (
  machine: Security.SecurityMachine,
  chainName: string,
): string[] => {
  // Shared across branches on purpose — reachability from a chain does not
  // depend on how that chain was reached, and it is what stops a cyclic machine
  // recursing forever.
  const visited = new Set<string>();
  const walk = (name: string, trail: string[]): string[] | null => {
    if (name === chainName) {
      return trail;
    }
    if (visited.has(name)) {
      return null;
    }
    visited.add(name);
    const chain = machine.ruleChains[name];
    if (!chain) {
      return null;
    }
    for (const next of execTargets(chain)) {
      const found = walk(next, [...trail, name]);
      if (found !== null) {
        return found;
      }
    }
    return null;
  };
  const trail = walk(machine.entryChainName, []);
  return trail === null ? [] : dropLeadingHidden(machine, trail);
};

/** Every chain a chain hands off to, by rule and by its default action. */
const execTargets = (chain: Security.RuleChain): string[] => [
  ...chain.rules.flatMap((rule) =>
    rule.action.type === 'exec' ? [rule.action.chainName] : [],
  ),
  ...(chain.defaultAction.type === 'exec'
    ? [chain.defaultAction.chainName]
    : []),
];

/**
 * Drop the leading run of `hideInUi` chains, the same rule
 * `setupStateForDecidingRule` applies when it builds a trail from a rule path.
 */
const dropLeadingHidden = (
  machine: Security.SecurityMachine,
  trail: string[],
): string[] => {
  const firstVisible = trail.findIndex(
    (name) => !machine.ruleChains[name]?.hideInUi,
  );
  return firstVisible === -1 ? [] : trail.slice(firstVisible);
};
